import { Template } from '../types';
import { splitPdfIntoTemplatePages } from './pdfUtils';

const TEMPLATES_STORAGE_KEY = 'wensing-app-templates';
const DEFAULTS_LOADED_KEY = 'wensing-app-defaults-loaded';

export function saveTemplates(templates: Template[]): void {
  try {
    const serializedTemplates = templates.map(template => ({
      ...template,
      pdfData: arrayBufferToBase64(template.pdfData),
      pages: template.pages?.map(page => ({
        ...page,
        pdfData: arrayBufferToBase64(page.pdfData),
      })),
      createdAt: template.createdAt.toISOString(),
      updatedAt: template.updatedAt.toISOString(),
    }));

    localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(serializedTemplates));
  } catch (error) {
    console.error('Kon templates niet opslaan:', error);
    throw new Error('Kon templates niet opslaan');
  }
}

export function loadTemplates(): Template[] {
  try {
    const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return parsed.map((template: any) => ({
      ...template,
      pdfData: base64ToArrayBuffer(template.pdfData),
      pages: template.pages?.map((page: any) => ({
        ...page,
        pdfData: base64ToArrayBuffer(page.pdfData),
      })),
      createdAt: new Date(template.createdAt),
      updatedAt: new Date(template.updatedAt),
    }));
  } catch (error) {
    console.error('Kon templates niet laden:', error);
    return [];
  }
}

export function saveTemplate(template: Template): void {
  const templates = loadTemplates();
  const existingIndex = templates.findIndex(t => t.id === template.id);
  
  if (existingIndex >= 0) {
    templates[existingIndex] = template;
  } else {
    templates.push(template);
  }
  
  saveTemplates(templates);
}

export function removeTemplate(templateId: string): void {
  const templates = loadTemplates().filter(t => t.id !== templateId);
  saveTemplates(templates);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function getStorageUsage(): { used: number; available: number } {
  try {
    const total = 5 * 1024 * 1024;
    let used = 0;

    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length;
      }
    }

    return {
      used: used,
      available: total - used
    };
  } catch (error) {
    return { used: 0, available: 0 };
  }
}

export async function loadDefaultTemplates(): Promise<Template[]> {
  const defaultTemplates: { name: string; url: string; isBundle?: boolean }[] = [
    { name: 'FWa Bundel', url: '/templates/FWa_bundel.pdf', isBundle: true },
    { name: 'FWa Voorblad', url: '/templates/FWa_Voorblad.pdf' },
    { name: 'FWa Vervolgblad', url: '/templates/FWa_Vervolgblad.pdf' },
  ];

  const templates: Template[] = [];

  for (const defaultTemplate of defaultTemplates) {
    try {
      const response = await fetch(defaultTemplate.url);
      if (!response.ok) {
        console.warn(`Could not load default template: ${defaultTemplate.name}`);
        continue;
      }

      const pdfData = await response.arrayBuffer();

      if (defaultTemplate.isBundle) {
        // Split bundle into pages
        const { pages } = await splitPdfIntoTemplatePages(pdfData);
        templates.push({
          id: `default-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: defaultTemplate.name,
          pdfData,
          isBundle: true,
          pages,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      } else {
        // Single page template
        templates.push({
          id: `default-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: defaultTemplate.name,
          pdfData,
          isBundle: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }
    } catch (error) {
      console.error(`Error loading default template ${defaultTemplate.name}:`, error);
    }
  }

  return templates;
}

export function shouldLoadDefaults(): boolean {
  return !localStorage.getItem(DEFAULTS_LOADED_KEY);
}

export function markDefaultsAsLoaded(): void {
  localStorage.setItem(DEFAULTS_LOADED_KEY, 'true');
}